import React, { useState } from 'react';
import CopyIcon from './icons/CopyIcon';
import ClearIcon from './icons/ClearIcon';

interface TextAreaWithControlsProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  placeholder: string;
  byteCount: number;
  isError: boolean;
  readOnly?: boolean;
}

const TextAreaWithControls: React.FC<TextAreaWithControlsProps> = ({
  label,
  value,
  onChange,
  onClear,
  placeholder,
  byteCount,
  isError,
  readOnly = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const containerClasses = isError 
    ? 'border-red-400' 
    : 'border-gray-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500';
  
  const childBorderClasses = isError ? 'border-red-400' : 'border-gray-200';

  return (
    <div className={`flex flex-col h-full bg-white rounded-lg border transition-all duration-200 ${containerClasses}`}>
      <div className={`p-3 border-b ${childBorderClasses}`}>
        <div className="flex justify-between items-center">
          <label className="text-sm font-semibold text-gray-800">{label}</label>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCopy}
              className="text-gray-500 hover:text-blue-600 transition-colors text-sm flex items-center"
              aria-label="Copy content"
            >
              {copied ? <span className="text-blue-600 text-xs">Copied!</span> : <><CopyIcon className="mr-1" /> Copy</>}
            </button>
            <button
              onClick={onClear}
              className="text-gray-500 hover:text-blue-600 transition-colors"
              aria-label="Clear all content"
            >
              <ClearIcon />
            </button>
          </div>
        </div>
      </div>
      <div className="relative flex-grow">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          className={`w-full h-full p-4 bg-transparent text-gray-800 resize-none focus:outline-none font-mono text-sm placeholder-gray-400 ${readOnly ? 'cursor-not-allowed' : ''}`}
          spellCheck="false"
        />
      </div>
      <div className={`p-2 border-t ${childBorderClasses} text-right`}>
        <span className="text-xs text-gray-400 font-mono">
          {value.length.toLocaleString()} Chars <span className="text-gray-300 mx-1">|</span> {byteCount.toLocaleString()} Bytes
        </span>
      </div>
    </div>
  );
};

export default TextAreaWithControls;